import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FilterBar } from '@/components/common/FilterBar';
import { AsyncButton } from '@/components/common/AsyncButton';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import type { ProvidersSummaryRowDto } from '@/api/types/providerFunds';
import type { ProviderFundsConfig } from './config';
import { yesterdayIso } from './config';
import { renderNumber } from './renderers';

interface SummaryTabProps {
  config: ProviderFundsConfig;
  canViewRealCashBalance: boolean;
  canViewPeriodLocking: boolean;
  onOpenTransactions: (providerId: string, providerName: string) => void;
  onOpenDetails: (providerId: string, providerName: string) => void;
  onOpenLocking: (providerId: string, providerName: string) => void;
}

function num(v: number | null | undefined): number {
  return v == null || Number.isNaN(v) ? 0 : v;
}

export function SummaryTab({
  config,
  canViewRealCashBalance,
  canViewPeriodLocking,
  onOpenTransactions,
  onOpenDetails,
  onOpenLocking,
}: SummaryTabProps) {
  const toast = useToast();
  const yesterday = yesterdayIso();
  const [to, setTo] = useState<string>(yesterday);
  const [applied, setApplied] = useState<string>(yesterday);

  const money = useQuery({
    queryKey: [config.key, 'summary', 'money', applied],
    queryFn: () => config.api.getSummary({ to: applied }),
  });
  const internals = useQuery({
    queryKey: [config.key, 'summary', 'internals', applied],
    queryFn: () => config.api.getInternalProviders!({ to: applied }),
    enabled: config.hasInternalsGrid,
  });

  function fetchData() {
    setApplied(to);
  }

  async function extract() {
    try {
      await config.api.downloadSummaryExcel({ to: applied }, config.summaryExcelName);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to export the summary.');
    }
  }

  const columns = useMemo<DataGridColumn<ProvidersSummaryRowDto>[]>(() => {
    const cols: DataGridColumn<ProvidersSummaryRowDto>[] = [
      { key: 'providerId', title: 'providerId', defaultVisible: false, hideable: false, filterable: false },
      { key: 'linkedProviders', title: 'linkedProviders', defaultVisible: false, hideable: false, filterable: false, value: (r) => (r.linkedProviders ?? []).join(', ') },
      { key: 'name', title: 'Provider', value: (r) => r.name },
      { key: 'type', title: 'Type', value: (r) => r.type },
      { key: 'currency', title: 'Currency', value: (r) => r.currency },
      { key: 'cashBookBalance', title: 'Cash Book Balance', className: 'text-end', value: (r) => r.cashBookBalance, cell: (r) => renderNumber(r.cashBookBalance) },
    ];
    if (canViewRealCashBalance) {
      cols.push({ key: 'balance', title: 'Real Cash Balance', className: 'text-end', value: (r) => r.balance, cell: (r) => renderNumber(r.balance) });
    }
    cols.push(
      { key: 'availableBalance', title: 'Available Balance', className: 'text-end', value: (r) => r.availableBalance, cell: (r) => renderNumber(r.availableBalance) },
      { key: 'withdrawableBalance', title: 'Withdrawable Balance', className: 'text-end', value: (r) => r.withdrawableBalance, cell: (r) => renderNumber(r.withdrawableBalance) },
      { key: 'cashBookBalanceRc', title: 'Cash Book Balance RC', className: 'text-end', value: (r) => r.cashBookBalanceRc, cell: (r) => renderNumber(r.cashBookBalanceRc) },
    );
    if (canViewRealCashBalance) {
      cols.push(
        { key: 'balanceRc', title: 'Real Cash Balance RC', className: 'text-end', value: (r) => r.balanceRc, cell: (r) => renderNumber(r.balanceRc) },
        { key: 'discrepancyRc', title: <span title="Difference between the Cash book balance RC and the Real Cash balance RC.">Discrepancy RC</span>, className: 'text-end', value: (r) => r.discrepancyRc, cell: (r) => renderNumber(r.discrepancyRc) },
      );
    }
    cols.push(
      { key: 'availableBalanceRc', title: 'Available Balance RC', className: 'text-end', value: (r) => r.availableBalanceRc, cell: (r) => renderNumber(r.availableBalanceRc) },
      { key: 'withdrawableBalanceRc', title: 'Withdrawable Balance RC', className: 'text-end', value: (r) => r.withdrawableBalanceRc, cell: (r) => renderNumber(r.withdrawableBalanceRc) },
      { key: 'ownership', title: 'Ownership', value: (r) => r.ownership },
      { key: 'concentration', title: 'Concentration', className: 'text-end', value: (r) => r.concentration, cell: (r) => renderNumber(r.concentration) },
    );
    return cols;
  }, [canViewRealCashBalance]);

  // The visible (non-hidden) column keys, so the totals footer aligns with the body.
  const visibleKeys = useMemo(() => columns.filter((c) => c.defaultVisible !== false).map((c) => c.key), [columns]);

  function totalsFooter(rows: ProvidersSummaryRowDto[]) {
    const sum = (sel: (r: ProvidersSummaryRowDto) => number) => rows.reduce((a, r) => a + num(sel(r)), 0);
    const totals: Record<string, string> = {
      cashBookBalanceRc: renderNumber(sum((r) => r.cashBookBalanceRc)),
      balanceRc: renderNumber(sum((r) => r.balanceRc)),
      discrepancyRc: renderNumber(sum((r) => r.discrepancyRc)),
      availableBalanceRc: renderNumber(sum((r) => r.availableBalanceRc)),
      withdrawableBalanceRc: renderNumber(sum((r) => r.withdrawableBalanceRc)),
    };
    return (
      <tr className="fw-bold">
        {visibleKeys.map((key, i) => {
          const align = columns.find((c) => c.key === key)?.className;
          let content = '-';
          if (i === 0) content = '';
          else if (i === 1) content = 'Total';
          else if (totals[key] !== undefined) content = totals[key];
          return (
            <td key={key} className={align}>
              {content}
            </td>
          );
        })}
        {/* Actions column */}
        <td>-</td>
      </tr>
    );
  }

  function rowActions(row: ProvidersSummaryRowDto) {
    // Legacy (providers-assets-listing.js wireRowActions) renders text-label
    // action buttons, not icons: "Transactions", "Fees", and "Lock".
    return (
      <div className="d-flex gap-1">
        <button
          type="button"
          className="btn btn-sm btn-outline-primary text-nowrap"
          title="View transactions"
          onClick={() => onOpenTransactions(row.providerId, row.name)}
        >
          Transactions
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-dark text-nowrap"
          title="View details and fees"
          onClick={() => onOpenDetails(row.providerId, row.name)}
        >
          Fees
        </button>
        {config.hasLockRowAction && canViewPeriodLocking && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary text-nowrap"
            title="Period locking"
            onClick={() => onOpenLocking(row.providerId, row.name)}
          >
            Lock
          </button>
        )}
      </div>
    );
  }

  const showMoneyGrid = !config.moneyGridPermGated || canViewRealCashBalance;

  const fields = [
    {
      node: <DateRangePicker key="to" label="To" mode="single" value={to} maxDate={yesterday} onChange={setTo} />,
      pinned: true,
      width: 160,
    },
  ];
  const actions = (
    <>
      <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-search" onClick={fetchData} loading={money.isFetching || internals.isFetching}>
        Fetch
      </AsyncButton>
      <AsyncButton className="btn btn-outline-primary btn-sm" icon="fa fa-download" onClick={extract}>
        Extract
      </AsyncButton>
    </>
  );

  return (
    <div>
      <div className="mb-4">
        <FilterBar card={false} fields={fields} actions={actions} inlineActions />
      </div>

      {showMoneyGrid && (
        <div className="mb-4">
          <h5 className="text-center">{config.summarySectionTitle}</h5>
          <DataGrid
            columns={columns}
            data={money.data ?? []}
            rowKey={(r) => r.providerId}
            footerTop={totalsFooter(money.data ?? [])}
            rowActions={rowActions}
            onRowClick={(r) => onOpenTransactions(r.providerId, r.name)}
            emptyMessage={money.isLoading ? 'Loading…' : 'No data available'}
          />
        </div>
      )}

      {config.hasInternalsGrid && (
        <div>
          <h5 className="text-center">Internals</h5>
          <DataGrid
            columns={columns}
            data={internals.data ?? []}
            rowKey={(r) => r.providerId}
            footerTop={totalsFooter(internals.data ?? [])}
            rowActions={rowActions}
            onRowClick={(r) => onOpenTransactions(r.providerId, r.name)}
            emptyMessage={internals.isLoading ? 'Loading…' : 'No data available'}
          />
        </div>
      )}
    </div>
  );
}
